
import React, { useEffect, useState } from "react";
import TdLink from "../../components/TdLink";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faPenToSquare,
  faCircleMinus,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { getAllAccessGroups, deleteAccessGroup } from "../../api/AccessGroups";
import { useAuth, Roles } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import { getOrganizationDisplayName } from "../../helpers/CustomerDisplay";
import DataTable from "../../components/DataTable";
import { useTranslation } from "react-i18next";

const AccessGroupsPage = () => {
  const { t } = useTranslation();
  const { getAccessToken, hasRole } = useAuth();
  const [accessGroups, setAccessGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [accessGroupsLoaded, setAccessGroupsLoaded] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => { },
  });

  useEffect(() => {
    handleGetAccessGroups();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, []);

  const handleGetAccessGroups = async () => {
    try {
      const accessAccessGroup = await getAccessToken();
      const newAccessGroups = await getAllAccessGroups(accessAccessGroup);
      setAccessGroups(newAccessGroups);
      setAccessGroupsLoaded(true);
    } catch (error) {
        console.error(error);
        toast.error(error?.message);
    }
  };


  const handleDeleteAccessGroup = async (accessGroupPublicId) => {
    const toastId = toast.loading(t("toast.deleting", { entity: t("entity.accessGroup") }));
    try {
      const accessAccessGroup = await getAccessToken();
      await deleteAccessGroup(accessGroupPublicId, accessAccessGroup);
      const newAccessGroups = await getAllAccessGroups(accessAccessGroup);
      setAccessGroups(newAccessGroups);
      toast.success(t("toast.deleted", { entity: t("entity.accessGroup") }), { id: toastId });
    } catch (error) {
        toast.error(error?.message, { id: toastId });
      console.error(error);
    }
  };

  const columns = [
    {
      key: "name",
      header: t("common.name"),
      sortable: true,
      rawCell: true,
      getSortValue: (accessGroup) => accessGroup.name ?? "",
      render: (accessGroup) => (
        <TdLink to={`/accessGroup/${accessGroup.accessGroupPublicId}`}>
          {accessGroup.name || ""}
        </TdLink>
      ),
    },
    {
      key: "description",
      header: t("common.description"),
      sortable: true,
      rawCell: true,
      getSortValue: (accessGroup) => accessGroup.description ?? "",
      render: (accessGroup) => (
        <TdLink to={`/accessGroup/${accessGroup.accessGroupPublicId}`}>
          {accessGroup.description || ""}
        </TdLink>
      ),
    },
    {
      key: "organizationName",
      header: t("entity.organization"),
      sortable: true,
      rawCell: true,
      hidden: true,
      getSortValue: (accessGroup) => getOrganizationDisplayName(accessGroup),
      render: (accessGroup) => (
        <TdLink to={`/accessGroup/${accessGroup.accessGroupPublicId}`}>
          {getOrganizationDisplayName(accessGroup)}
        </TdLink>
      ),
    },
    {
      key: "hasUniversalSiteAccess",
      header: t("table.globalAccess"),
      sortable: true,
      rawCell: true,
      hidden: !hasRole(Roles.Admin),
      getSortValue: (accessGroup) =>
        accessGroup.hasUniversalSiteAccess ? "yes" : "no",
      render: (accessGroup) => (
        <TdLink to={`/accessGroup/${accessGroup.accessGroupPublicId}`}>
          {accessGroup.hasUniversalSiteAccess ? t("common.yes") : t("common.no")}
        </TdLink>
      ),
    },
    {
      key: "actions",
      header: "",
      sortable: false,
      searchable: false,
      rawCell: true,
      render: (accessGroup) => (
        <td>
          <div className="flex justify-end">
            <CustomDropdown
              isRight={true}
              ContentComponent={() => (
                <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                  <NavLink
                    className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                    to={`/accessGroup/${accessGroup.accessGroupPublicId}`}
                  >
                    <FontAwesomeIcon icon={faPenToSquare} /> {t("common.edit")}
                  </NavLink>
                  <button
                    className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                    onClick={() => {
                      setConfirmState({
                        isOpen: true,
                        title: t("confirm.deleteEntity", {
                          entity: t("entity.accessGroup"),
                          name: accessGroup.name,
                        }),
                        confirm: () => {
                          handleDeleteAccessGroup(accessGroup.accessGroupPublicId);
                        },
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                  </button>
                </div>
              )}
            >
              <div className="w-8 h-6 flex justify-end bg-red items-center cursor-accessGrouper">
                <FontAwesomeIcon icon={faEllipsis} />
              </div>
            </CustomDropdown>
          </div>
        </td>
      ),
    },
  ];

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            
          <div className="absolute inset-0 pointer-events-none">
              <LoadingTablePage title={t("entity.accessGroups")} loading={!accessGroupsLoaded} />
            </div>

            <div className={`${!accessGroupsLoaded ? "loading" : ""} page-content`}>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">{t("entity.accessGroups")}</h2>
                <NavLink
                  className="btn btn-primary"
                  to={`/accessGroup/create`}
                >
                  <FontAwesomeIcon icon={faPlus} /> {t("pages.createAccessGroup")}
                </NavLink>
              </div>

              <div className="rounded-md shadow-md w-full p-4">
                <DataTable
                  rows={accessGroups}
                  columns={columns}
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder={t("common.search")}
                  rowKey={(accessGroup, index) =>
                    accessGroup.accessGroupPublicId || index
                  }
                  emptyColSpan={hasRole(Roles.Admin) ? 5 : 3}
                />
              </div>
            </div>
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default AccessGroupsPage;

